import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type SxProps,
  type Theme
} from '@mui/material';

export type SaColumnAlign = 'left' | 'center' | 'right';

export type SaColumn<T> = {
  id: string;
  label: React.ReactNode;
  align?: SaColumnAlign;
  width?: number | string;
  minWidth?: number | string;
  render: (row: T) => React.ReactNode;
  headerSx?: SxProps<Theme>;
  cellSx?: SxProps<Theme>;
};

type SaDataTableProps<T> = {
  columns: SaColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  minWidth?: number;
};

const headerCellSx: SxProps<Theme> = {
  bgcolor: 'var(--color-background-secondary)',
  color: 'var(--color-text-tertiary)',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--color-border-primary)',
  py: 1.5,
  px: 2,
  whiteSpace: 'nowrap',
  lineHeight: 1.4
};

const bodyCellSx: SxProps<Theme> = {
  borderBottom: '1px solid var(--color-border-secondary)',
  py: 1.5,
  px: 2,
  fontSize: 14,
  color: 'var(--color-text-secondary)',
  verticalAlign: 'middle'
};

/**
 * MUI Super Admin table.
 * `align: 'right'` uses an absolute fill layer so Actions header + control
 * share the same right padding edge (table % width flex is unreliable).
 */
export function SaDataTable<T>({
  columns,
  rows,
  getRowId,
  emptyMessage = 'No results',
  onRowClick,
  minWidth = 720
}: SaDataTableProps<T>) {
  const renderCellContent = (
    align: SaColumnAlign,
    content: React.ReactNode
  ) => {
    if (align !== 'right') return content;

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 2,
          boxSizing: 'border-box'
        }}
      >
        {content}
      </Box>
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        bgcolor: 'var(--color-background-card)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: 3,
        boxShadow: '0 1px 2px var(--color-shadow)',
        overflowX: 'auto'
      }}
    >
      <Table
        sx={{
          minWidth,
          tableLayout: 'fixed',
          width: '100%',
          borderCollapse: 'separate'
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map(col => {
              const align = col.align ?? 'left';
              const isRight = align === 'right';
              return (
                <TableCell
                  key={col.id}
                  align={isRight ? undefined : align}
                  sx={{
                    ...headerCellSx,
                    width: col.width,
                    minWidth: col.minWidth ?? col.width,
                    ...(isRight
                      ? {
                          position: 'relative',
                          height: 48,
                          p: 0,
                          overflow: 'visible'
                        }
                      : {}),
                    ...((col.headerSx as object) || {})
                  }}
                >
                  {renderCellContent(align, col.label)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={getRowId(row)}
              hover
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                '&:last-child td': { borderBottom: 0 },
                '&:hover': { bgcolor: 'var(--color-background-hover)' }
              }}
            >
              {columns.map(col => {
                const align = col.align ?? 'left';
                const isRight = align === 'right';
                return (
                  <TableCell
                    key={col.id}
                    align={isRight ? undefined : align}
                    sx={{
                      ...bodyCellSx,
                      width: col.width,
                      minWidth: col.minWidth ?? col.width,
                      ...(isRight
                        ? {
                            position: 'relative',
                            height: 64,
                            p: 0,
                            overflow: 'visible'
                          }
                        : {}),
                      ...((col.cellSx as object) || {})
                    }}
                  >
                    {renderCellContent(align, col.render(row))}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{
                  py: 8,
                  color: 'var(--color-text-muted)',
                  borderBottom: 0
                }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
